import { parseCreatedTask } from "./task-schedule";
import { completionStatus } from "./completion-status";
import { parseSseFrame, terminalSseTail } from "./sse-frames";
import { RequestTimeoutError } from "./request-errors";
import type { BrowserScreenshot, GeneratedFile, GeneratedImage, ImageGenerationState, ModelProgressState, ScriptExecution, StreamEvent, WebResearchState } from "../types";

export class StreamInterruptedError extends Error {
  constructor() {
    super("The response stream ended before its completion marker.");
    this.name = "StreamInterruptedError";
  }
}

const getNestedContent = (value: unknown): StreamEvent => {
  if (typeof value === "string") return { content: value };
  if (!value || typeof value !== "object") return {};

  const data = value as Record<string, unknown>;
  const event: StreamEvent = {};
  const createdTask = parseCreatedTask(data.scheduled_task);
  if (createdTask) event.createdTask = createdTask;
  const reset = data.response_reset as { content_length?: unknown; reasoning_length?: unknown } | undefined;
  if (typeof reset?.content_length === "number" && Number.isInteger(reset.content_length) && reset.content_length >= 0 && typeof reset.reasoning_length === "number" && Number.isInteger(reset.reasoning_length) && reset.reasoning_length >= 0) event.responseReset = { contentLength: reset.content_length, reasoningLength: reset.reasoning_length };
  const sync = data.conversation_sync as { conversation_id?: unknown } | undefined;
  if (typeof sync?.conversation_id === "string") event.serverConversationId = sync.conversation_id;
  const context = data.context_management as { phase?: unknown } | undefined;
  if (context?.phase === "compacting" || context?.phase === "continuing" || context?.phase === "ready") event.contextPhase = context.phase;
  if (data.generated_image && typeof data.generated_image === "object") event.generatedImage = data.generated_image as GeneratedImage;
  if (data.image_generation && typeof data.image_generation === "object") event.imageGeneration = data.image_generation as ImageGenerationState;
  if (data.web_research && typeof data.web_research === "object") event.webResearch = data.web_research as WebResearchState;
  if (data.browser_screenshot && typeof data.browser_screenshot === "object") event.browserScreenshot = data.browser_screenshot as BrowserScreenshot;
  if (data.generated_file && typeof data.generated_file === "object") event.generatedFile = data.generated_file as GeneratedFile;
  if (data.script_execution && typeof data.script_execution === "object") event.scriptExecution = data.script_execution as ScriptExecution;
  const progress = data.model_progress as ModelProgressState | undefined;
  if (progress && ["reasoning", "answering", "preparing_tool", "waiting", "recovering"].includes(progress.phase)) event.modelProgress = progress;
  const choices = Array.isArray(data.choices) ? data.choices as Array<Record<string, unknown>> : [];
  const delta = choices[0]?.delta as Record<string, unknown> | undefined;
  const message = choices[0]?.message as Record<string, unknown> | undefined;
  const responseDelta = data.delta && typeof data.delta === "object" ? data.delta as Record<string, unknown> : undefined;
  const type = typeof data.type === "string" ? data.type : "";
  const content = data.response ?? data.content ?? delta?.content ?? message?.content ?? responseDelta?.text ??
    (typeof data.delta === "string" && !type.includes("reasoning") ? data.delta : undefined);
  const reasoning = data.reasoning ?? delta?.reasoning_content ?? delta?.reasoning ?? message?.reasoning_content ?? message?.reasoning ??
    (typeof data.delta === "string" && type.includes("reasoning") ? data.delta : undefined);
  if (typeof content === "string") event.content = content;
  if (typeof reasoning === "string") event.reasoning = reasoning;
  if (data.usage && typeof data.usage === "object") event.usage = data.usage as Record<string, number>;
  if (data.cancelled === true) event.cancelled = true;
  const completion = completionStatus(data);
  if (completion.finished) event.done = true;
  const error = data.error && typeof data.error === "object" ? data.error as Record<string, unknown> : undefined;
  if (typeof data.error === "string" || typeof error?.message === "string" || completion.errorCode) {
    event.error = typeof data.error === "string" ? data.error : typeof error?.message === "string" ? error.message : "The model could not finish its answer.";
    event.errorCode = typeof data.error_code === "string" ? data.error_code : typeof error?.code === "string" ? error.code : completion.errorCode;
    event.retryAfter = typeof data.retry_after === "number" ? data.retry_after : undefined;
  }
  return event;
};

export const parseSseData = (data: string): StreamEvent => {
  if (!data) return {};
  if (data.trim() === "[DONE]") return { done: true };
  try {
    return getNestedContent(JSON.parse(data));
  } catch {
    return { content: data };
  }
};

export const consumeChatStream = async (
  response: Response,
  onEvent: (event: StreamEvent) => void,
  options: { signal?: AbortSignal; idleTimeoutMs?: number; network?: EventTarget } = {},
): Promise<void> => {
  if (!response.body) throw new Error("The model returned an empty stream.");

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let completed = false;

  const emit = (data: string, cursor?: number) => {
    const event = parseSseData(data);
    if (cursor != null) event.cursor = cursor;
    if (event.done) completed = true;
    onEvent(event);
  };

  const emitBlock = (block: string) => {
    const { data, cursor } = parseSseFrame(block);
    if (data) emit(data, cursor);
  };

  try {
    while (true) {
      let timer: ReturnType<typeof setTimeout> | undefined;
      let onAbort: (() => void) | undefined;
      let onOffline: (() => void) | undefined;
      const deadline = new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new RequestTimeoutError()), options.idleTimeoutMs ?? 60_000);
        onAbort = () => reject(new DOMException("Aborted", "AbortError"));
        if (options.signal?.aborted) onAbort();
        else options.signal?.addEventListener("abort", onAbort, { once: true });
        onOffline = () => reject(new StreamInterruptedError());
        options.network?.addEventListener("offline", onOffline, { once: true });
      });
      const { value, done } = await Promise.race([reader.read(), deadline]).finally(() => {
        clearTimeout(timer);
        if (onAbort) options.signal?.removeEventListener("abort", onAbort);
        if (onOffline) options.network?.removeEventListener("offline", onOffline);
      });
      buffer += decoder.decode(value, { stream: !done });
      const blocks = buffer.split(/\r?\n\r?\n/);
      buffer = blocks.pop() ?? "";

      for (const block of blocks) {
        emitBlock(block);
        if (completed) break;
      }
      const terminal = !completed && terminalSseTail(buffer);
      if (terminal) { emit(terminal.data, terminal.cursor); buffer = ""; }
      if (done || completed) break;
    }

    const tail = buffer.trim();
    if (tail && !completed) {
      if (tail.includes("data:")) emitBlock(tail);
      else emit(tail);
    }
  } finally {
    void reader.cancel().catch(() => undefined);
  }

  if (!completed) throw new StreamInterruptedError();
};
